import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import styled from "styled-components";

import {
  SUBTITLES_BACKGROUND_COLOR,
  SUBTITLES_FONT_SIZE_MAX,
  SUBTITLES_FONT_SIZE_MIN,
  SUBTITLES_FONT_SIZE_SQUAREVIDEO_MULTIPLIER,
  SUBTITLES_FONT_SIZE_WIDTH_BASED_MULTIPLIER
} from "../../constants";
import { useEmbedParametersContext } from "../../contexts/embedParametersContext";
import { SUBTITLES_BACKGROUND } from "../../enums";
import i18n from "../../service/i18n";
import { usePlayerStore } from "../../store/usePlayerStore";

const isSquareContainer = playerContainerSize =>
  playerContainerSize.h === playerContainerSize.w;

const isVerticalContainer = (playerContainerSize: ElementSize) =>
  playerContainerSize.h > playerContainerSize.w;

const isHorizontalOrSquareVideoInPortraitMode = (
  playerContainerSize: ElementSize,
  aspectRatio?: VideoAspectRatio
) => {
  const isHorizontalVideo = aspectRatio
    ? aspectRatio.w >= aspectRatio.h
    : false;

  return isVerticalContainer(playerContainerSize) && isHorizontalVideo;
};

const parseAspectRatio = (
  aspectRatio?: string
): VideoAspectRatio | undefined => {
  if (aspectRatio) {
    const splittedRatio = aspectRatio.split(":");
    return {
      h: parseInt(splittedRatio[1], 10),
      w: parseInt(splittedRatio[0], 10)
    };
  }
};

const resolveFontSize = (
  subtitlesSizeMultiplier: number,
  playerContainerSize: ElementSize
) => {
  const responsiveFontSize =
    Math.max(playerContainerSize.h, playerContainerSize.w) *
    SUBTITLES_FONT_SIZE_WIDTH_BASED_MULTIPLIER;

  let fontSize =
    responsiveFontSize > SUBTITLES_FONT_SIZE_MAX
      ? SUBTITLES_FONT_SIZE_MAX
      : responsiveFontSize < SUBTITLES_FONT_SIZE_MIN
        ? SUBTITLES_FONT_SIZE_MIN
        : responsiveFontSize;

  if (isSquareContainer(playerContainerSize)) {
    fontSize *= SUBTITLES_FONT_SIZE_SQUAREVIDEO_MULTIPLIER;
  }

  fontSize *= subtitlesSizeMultiplier;

  return Math.round(fontSize);
};

const resolveSubtitlesPosition = (
  isControlsVisible: boolean,
  isMobile: boolean,
  playerContainerSize?: ElementSize,
  aspectRatio?: VideoAspectRatio
) => {
  const minPosition = isControlsVisible ? (isMobile ? 32 : 80) : 0;

  if (
    playerContainerSize &&
    aspectRatio &&
    isHorizontalOrSquareVideoInPortraitMode(playerContainerSize, aspectRatio)
  ) {
    const videoHeight = (playerContainerSize.w / aspectRatio.w) * aspectRatio.h;
    const videoBottomBorderPosition =
      playerContainerSize.h / 2 - videoHeight / 2;

    return Math.max(minPosition, videoBottomBorderPosition);
  }

  return minPosition;
};

const resolveMarginBottom = (
  isFullscreen: boolean,
  isMobile: boolean,
  playerContainerSize: ElementSize,
  aspectRatio?: VideoAspectRatio
) => {
  if (
    isHorizontalOrSquareVideoInPortraitMode(playerContainerSize, aspectRatio)
  ) {
    return isMobile ? (isFullscreen ? 16 : 8) : 32;
  }
  if (isVerticalContainer(playerContainerSize)) {
    return 32;
  }
  if (isMobile) {
    return isSquareContainer(playerContainerSize) || isFullscreen ? 16 : 8;
  }
  if (isSquareContainer(playerContainerSize) || playerContainerSize.w > 1000) {
    return 32;
  }
  return 16;
};

const resolveVideoImageSize = (
  playerContainerSize?: ElementSize,
  aspectRatio?: VideoAspectRatio
): ElementSize | undefined => {
  if (!playerContainerSize) return undefined;
  if (!aspectRatio) return playerContainerSize;

  const containerRatio =
    Math.round((playerContainerSize.w / playerContainerSize.h) * 100) / 100;
  const imageRatio = Math.round((aspectRatio.w / aspectRatio.h) * 100) / 100;

  if (containerRatio === imageRatio) {
    return playerContainerSize;
  } else if (containerRatio > imageRatio) {
    // The container is wider than the video image
    return {
      h: playerContainerSize.h,
      w: Math.round(playerContainerSize.h * imageRatio)
    };
  } else {
    return {
      h: Math.round(playerContainerSize.w / imageRatio),
      w: playerContainerSize.w
    };
  }
};

const SubtitleRow = styled.span<{
  fontSize?: number;
  subtitlesBackgroundColor: SUBTITLES_BACKGROUND | "transparent";
}>`
  background: ${({ subtitlesBackgroundColor }) =>
    subtitlesBackgroundColor === "transparent"
      ? "transparent"
      : SUBTITLES_BACKGROUND_COLOR[subtitlesBackgroundColor]};

  /* Note: Subtitle color is white unless background is white */
  color: ${({ subtitlesBackgroundColor }) =>
    subtitlesBackgroundColor === SUBTITLES_BACKGROUND.WHITE
      ? "black"
      : "white"};
  font-family: YleTiresias, sans-serif !important;
  ${({ fontSize }) => (fontSize ? `font-size: ${fontSize}px;` : "")};
  line-height: normal;
  pointer-events: all;
  text-align: center;
  text-shadow: ${({ subtitlesBackgroundColor }) =>
    subtitlesBackgroundColor === SUBTITLES_BACKGROUND.NONE
      ? "1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000"
      : "none"};
  transition: bottom 0.3s ease-in-out;
  white-space: pre-line;
  padding: 0.05em 0.25em;
`;

const SubtitleWrapper = styled.div<{
  marginBottom: number;
  bottomPosition: number;
}>`
  align-items: center;
  align-self: center;
  bottom: ${({ bottomPosition }) => `${bottomPosition}px`};
  display: flex;
  flex-direction: column;
  ${({ marginBottom }) =>
    marginBottom ? `margin-bottom: ${marginBottom}px;` : ""};
  pointer-events: none;
  position: absolute;
  width: 100%;
`;

type ElementSize = {
  h: number;
  w: number;
};

type VideoAspectRatio = { w: number; h: number };

const resolveRowBackground = (cue, subtitlesBackgroundColor) =>
  cue.trim().length === 0 ? "transparent" : subtitlesBackgroundColor;

export const Subtitles = () => {
  const { useStoreState } = usePlayerStore();
  const aspectRatio = useStoreState.aspectRatio();
  const device = useStoreState.device();
  const isControlsVisible = useStoreState.isControlsVisible();
  const isFullscreen = useStoreState.isFullscreen();
  const playerElement = useStoreState.playerElement();
  const subtitlesBackground = useStoreState.subtitlesBackground();
  const subtitlesCue = useStoreState.subtitlesCue();
  const subtitlesSizeMultiplier = useStoreState.subtitlesSizeMultiplier();

  const { alwaysFocusScreenReaderSubtitles, language } =
    useEmbedParametersContext();

  const [playerContainerSize, setPlayerContainerSize] = useState<ElementSize>();
  const [videoImageSize, setVideoImageSize] = useState<ElementSize>();
  const [focused, setFocused] = useState(false);

  const isMounted = useRef(true);

  const aspectRatioParsed = useMemo(() => {
    return parseAspectRatio(aspectRatio);
  }, [aspectRatio]);

  const resolvePlayerContainerSize = useCallback(() => {
    if (playerElement) {
      const observer = new IntersectionObserver(nodes => {
        observer.disconnect();
        const rect = nodes[0].boundingClientRect;
        if (isMounted.current) {
          setPlayerContainerSize({
            h: Math.round(rect.height),
            w: Math.round(rect.width)
          });
        }
      });
      observer.observe(playerElement);
    }
  }, [playerElement]);

  useEffect(() => {
    if (playerElement) {
      resolvePlayerContainerSize();
      window.addEventListener("resize", resolvePlayerContainerSize);

      return () => {
        isMounted.current = false;
        window.removeEventListener("resize", resolvePlayerContainerSize);
      };
    }
  }, [playerElement, resolvePlayerContainerSize]);

  useEffect(() => {
    // This fixes font size after a call to player api setAspectRatio()
    setTimeout(resolvePlayerContainerSize);
  }, [aspectRatio, resolvePlayerContainerSize]);

  useEffect(resolvePlayerContainerSize, [
    isFullscreen,
    resolvePlayerContainerSize
  ]);

  useEffect(() => {
    setVideoImageSize(
      resolveVideoImageSize(playerContainerSize, aspectRatioParsed)
    );
  }, [aspectRatioParsed, playerContainerSize]);

  const fontSize = useMemo(() => {
    if (videoImageSize) {
      return resolveFontSize(subtitlesSizeMultiplier, videoImageSize);
    }
  }, [videoImageSize, subtitlesSizeMultiplier]);

  const marginBottom = useMemo(() => {
    if (playerContainerSize) {
      return resolveMarginBottom(
        isFullscreen,
        device.isMobile,
        playerContainerSize,
        aspectRatioParsed
      );
    }
    return 0;
  }, [isFullscreen, device, playerContainerSize, aspectRatioParsed]);

  const subtitlesPosition = useMemo(() => {
    return resolveSubtitlesPosition(
      isControlsVisible,
      device.isMobile,
      playerContainerSize,
      aspectRatioParsed
    );
  }, [isControlsVisible, device, playerContainerSize, aspectRatioParsed]);

  const onFocus = () => setFocused(true);
  const onBlur = () => setFocused(false);

  return playerContainerSize ? (
    <SubtitleWrapper
      aria-label={i18n("tekstitykset", { language })}
      aria-live={focused || alwaysFocusScreenReaderSubtitles ? "polite" : "off"}
      bottomPosition={subtitlesPosition}
      data-testid="subtitles-wrapper"
      marginBottom={marginBottom}
      onBlur={onBlur}
      onFocus={onFocus}
      role="region"
      tabIndex={0}>
      {subtitlesCue !== "" &&
        subtitlesCue.split("\n").map((cue, index) => (
          <SubtitleRow
            fontSize={fontSize}
            key={cue + index}
            subtitlesBackgroundColor={resolveRowBackground(
              cue,
              subtitlesBackground
            )}
            tabIndex={-1}>
            {cue}
          </SubtitleRow>
        ))}
    </SubtitleWrapper>
  ) : null;
};
